'use client';

import { useCallback } from 'react';

import {
  parseHash,
  serializeHash,
  useUrlHash,
  type HashHistoryMode
} from '@/hooks/use-url-hash';

interface HashDialogOptions {
  /**
   * History strategy when opening/closing. Default `'replace'` so opening an
   * overlay never adds its own back-stop: browser Back leaves the current
   * route (wherever it came from) instead of just closing the overlay in
   * place. Pass `'push'` when an overlay should behave like a standard
   * modal — Back closes it first, staying on the page.
   */
  history?: HashHistoryMode;
}

/**
 * Bind an exact-match overlay to the URL hash (`#edit`, `#assign`). A drop-in
 * replacement for `useState<boolean>` on any controlled overlay: pass the
 * returned `open`/`onOpenChange` straight to the Dialog/Sheet/etc.
 *
 * The hash holds a single key, so only one overlay is open at a time and
 * opening another closes this one automatically — no "close others" wiring.
 */
export function useHashDialog(key: string, options: HashDialogOptions = {}) {
  const { hash, setHash } = useUrlHash();
  const mode = options.history ?? 'replace';
  const open = parseHash(hash)?.key === key;

  const onOpenChange = useCallback(
    (next: boolean) => {
      setHash(next ? serializeHash(key) : '', mode);
    },
    [key, mode, setHash]
  );

  return { open, onOpenChange } as const;
}

/**
 * Bind a parameterized overlay to the URL hash (`#image/front`, `#print/qr`).
 * `param` is the first positional segment (or `null` when closed); `params`
 * exposes all segments; `openWith(...params)` opens with a specific payload.
 */
export function useHashParamDialog(
  key: string,
  options: HashDialogOptions = {}
) {
  const { hash, setHash } = useUrlHash();
  const mode = options.history ?? 'replace';
  const parsed = parseHash(hash);
  const open = parsed?.key === key;

  const openWith = useCallback(
    (...params: string[]) => {
      setHash(serializeHash(key, params), mode);
    },
    [key, mode, setHash]
  );

  const onOpenChange = useCallback(
    (next: boolean) => {
      if (!next) setHash('', mode);
    },
    [mode, setHash]
  );

  return {
    open,
    param: open ? (parsed?.params[0] ?? null) : null,
    params: open ? (parsed?.params ?? []) : [],
    openWith,
    onOpenChange
  } as const;
}
